import { useCallback, useMemo, useState, type ReactNode } from 'react';

import { Empty, Modal, Segmented, Spin } from 'antd';
import dayjs from 'dayjs';
import type { TFunction } from 'i18next';
import { CloudSun, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import {
  useGetOpenMeteoPointDailyQuery,
  useGetOpenMeteoPointHourlyQuery,
} from '@/features/dashboard/api/openMeteoApi';
import { DailyForecastCard } from '@/features/dashboard/components/DailyForecastCard';
import { WeatherLocationSearch } from '@/features/dashboard/components/WeatherLocationSearch';
import type { WeatherForecastListItem } from '@/features/dashboard/types/weatherForecast.types';
import { dailyOpenMeteoToUiRows } from '@/features/dashboard/utils/openMeteo/dailyToUiRows';
import { hourlyOpenMeteoToUiForecast } from '@/features/dashboard/utils/openMeteo/hourlyOpenMeteoToUiForecast';
import { setWeatherPoint } from '@/features/map/stores/mapSlice';
import { useAppDispatch } from '@/hooks/useAppDispatch';
import { shouldFetchOperationalApi } from '@/utils/dataSource';

export interface WeatherForecastModalProps {
  open: boolean;
  onClose: () => void;
  latStr: string;
  lonStr: string;
  /** Tên địa điểm gợi ý (nếu có). */
  placeHint?: string;
  /** Nhãn cấu hình (`VITE_WEATHER_LOCATION_DISPLAY_NAME`), ưu tiên hiển thị trong tiêu đề. */
  locationLabelOverride?: string | null;
}

function weatherIconUrl2x(icon: string): string {
  return `https://openweathermap.org/img/wn/${icon}@2x.png`;
}

function ForecastSlotCard({
  row,
  prevDt,
  t,
}: {
  row: WeatherForecastListItem & { key: string };
  prevDt: number | null;
  t: TFunction;
}) {
  const d = dayjs.unix(row.dt);
  const prevDay = prevDt != null ? dayjs.unix(prevDt).format('YYYY-MM-DD') : null;
  const showDayChip = !prevDay || prevDay !== d.format('YYYY-MM-DD');
  const w = row.weather?.[0];
  const popPct = row.pop != null ? Math.round(row.pop * 100) : null;
  const wind =
    row.wind?.speed != null
      ? `${(Math.round(row.wind.speed * 10) / 10).toFixed(1)} ${t('units.metersPerSecond')}`
      : null;

  return (
    <div
      style={{
        scrollSnapAlign: 'start',
        flex: '0 0 auto',
        width: 118,
        boxSizing: 'border-box',
        padding: '10px 10px 12px',
        borderRadius: 12,
        border: '1px solid var(--glass-border)',
        background:
          'linear-gradient(165deg, rgba(59, 130, 246, 0.1) 0%, var(--bg-card) 55%, var(--map-chrome-deep) 100%)',
      }}
    >
      {showDayChip ? (
        <div
          style={{
            fontSize: 10,
            fontWeight: 800,
            letterSpacing: '0.04em',
            textTransform: 'uppercase',
            color: 'var(--accent-blue)',
            marginBottom: 6,
          }}
        >
          {d.format('DD/MM')}
        </div>
      ) : (
        <div style={{ height: 17, marginBottom: 6 }} aria-hidden />
      )}
      <div
        style={{
          fontSize: 13,
          fontWeight: 800,
          fontVariantNumeric: 'tabular-nums',
          color: 'var(--text-primary)',
          marginBottom: 6,
        }}
      >
        {d.format('HH:mm')}
      </div>
      <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 6 }}>
        {w?.icon ? (
          <img
            src={weatherIconUrl2x(w.icon)}
            alt=""
            width={56}
            height={56}
            style={{ display: 'block' }}
          />
        ) : (
          <div style={{ width: 56, height: 56 }} />
        )}
      </div>
      <div
        style={{
          fontSize: 20,
          fontWeight: 800,
          fontVariantNumeric: 'tabular-nums',
          textAlign: 'center',
          marginBottom: 6,
          color: 'var(--text-primary)',
        }}
      >
        {Math.round(row.main.temp * 10) / 10}°
      </div>
      {w?.description ? (
        <div
          style={{
            fontSize: 11,
            lineHeight: 1.35,
            color: 'var(--text-secondary)',
            textAlign: 'center',
            minHeight: 30,
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}
        >
          {w.description}
        </div>
      ) : (
        <div style={{ minHeight: 30 }} />
      )}
      <div
        style={{
          marginTop: 8,
          paddingTop: 8,
          borderTop: '1px solid var(--glass-border)',
          fontSize: 10,
          fontWeight: 600,
          color: 'var(--text-secondary)',
          display: 'flex',
          flexDirection: 'column',
          gap: 4,
        }}
      >
        <span>
          {popPct != null
            ? t('weather.forecastSlotPop', { pct: popPct })
            : t('weather.forecastSlotPopUnknown')}
        </span>
        <span>{wind ?? '—'}</span>
      </div>
    </div>
  );
}

type ForecastViewMode = 'hourly' | 'daily';

/**
 * Dự báo theo tâm bản đồ — BFF Open-Meteo (`kind=hourly` | `kind=daily`).
 */
export function WeatherForecastModal({
  open,
  onClose,
  latStr,
  lonStr,
  placeHint,
  locationLabelOverride,
}: WeatherForecastModalProps) {
  const { t } = useTranslation();
  const fetchOps = shouldFetchOperationalApi();
  const dispatch = useAppDispatch();

  const [viewMode, setViewMode] = useState<ForecastViewMode>('hourly');

  const handleAfterModalOpenChange = useCallback((opened: boolean) => {
    if (!opened) setViewMode('hourly');
  }, []);

  const skipHourly = !open || !fetchOps || viewMode !== 'hourly';
  const skipDaily = !open || !fetchOps || viewMode !== 'daily';

  const hourlyQ = useGetOpenMeteoPointHourlyQuery(
    { lat: latStr, lon: lonStr },
    { skip: skipHourly },
  );
  const dailyQ = useGetOpenMeteoPointDailyQuery({ lat: latStr, lon: lonStr }, { skip: skipDaily });

  const rows = useMemo(() => {
    if (!hourlyQ.data?.hourly) return [];
    const list = hourlyOpenMeteoToUiForecast(hourlyQ.data.hourly, t);
    return list.map((item, index) => ({
      key: `${item.dt}-${index}`,
      ...item,
    }));
  }, [hourlyQ.data, t]);

  const dailyRows = useMemo(() => {
    if (!dailyQ.data?.daily) return [];
    return dailyOpenMeteoToUiRows(dailyQ.data.daily);
  }, [dailyQ.data]);

  const uniqueDayKeys = useMemo(() => {
    if (!rows.length) return [];
    const keys = new Set(rows.map((r) => dayjs.unix(r.dt).format('YYYY-MM-DD')));
    return [...keys].sort();
  }, [rows]);

  const [userDayKey, setUserDayKey] = useState<string | null>(null);

  const selectedDayKey = useMemo(() => {
    if (!uniqueDayKeys.length) return null;
    if (userDayKey && uniqueDayKeys.includes(userDayKey)) return userDayKey;
    return uniqueDayKeys[0] ?? null;
  }, [uniqueDayKeys, userDayKey]);

  const filteredRows = useMemo(() => {
    if (!rows.length) return [];
    if (!selectedDayKey) return rows;
    return rows.filter((r) => dayjs.unix(r.dt).format('YYYY-MM-DD') === selectedDayKey);
  }, [rows, selectedDayKey]);

  const cityName =
    locationLabelOverride?.trim() ||
    hourlyQ.data?.place_name ||
    dailyQ.data?.place_name ||
    placeHint?.trim();

  const titleMain = cityName
    ? t('kpi.weatherForecastModalTitlePlace', { place: cityName })
    : t('kpi.weatherForecastModalTitle');

  const dayFilter =
    viewMode === 'hourly' && uniqueDayKeys.length > 1 && selectedDayKey ? (
      <div style={{ marginBottom: 12 }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', marginBottom: 6 }}>
          {t('weather.forecastFilterDayLabel')}
        </div>
        <Segmented
          block
          size="small"
          value={selectedDayKey}
          onChange={(v) => setUserDayKey(String(v))}
          options={uniqueDayKeys.map((k) => ({
            value: k,
            label: dayjs(k).format('DD/MM'),
          }))}
        />
      </div>
    ) : null;

  const viewSwitch = fetchOps ? (
    <div style={{ marginBottom: 14 }}>
      <Segmented
        block
        size="small"
        value={viewMode}
        onChange={(v) => {
          setViewMode(v as ForecastViewMode);
          setUserDayKey(null);
        }}
        options={[
          { value: 'hourly', label: t('weather.forecastViewHourly') },
          { value: 'daily', label: t('weather.forecastViewDaily') },
        ]}
      />
    </div>
  ) : null;

  let body: ReactNode;
  if (!fetchOps) {
    body = <Empty description={t('kpi.weatherUnavailable')} />;
  } else if (viewMode === 'hourly') {
    if (hourlyQ.isFetching && !hourlyQ.data) {
      body = (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}>
          <Spin size="large" />
        </div>
      );
    } else if (hourlyQ.isError) {
      body = <Empty description={t('kpi.weatherError')} />;
    } else if (!hourlyQ.data || rows.length === 0) {
      body = <Empty description={t('kpi.weatherForecastEmpty')} />;
    } else {
      body = (
        <>
          {dayFilter}
          <div
            className="weather-forecast-strip"
            style={{
              display: 'flex',
              gap: 10,
              overflowX: 'auto',
              paddingBottom: 6,
              scrollSnapType: 'x mandatory',
              WebkitOverflowScrolling: 'touch',
            }}
          >
            {filteredRows.map((row, idx) => (
              <ForecastSlotCard
                key={row.key}
                row={row}
                prevDt={idx > 0 ? filteredRows[idx - 1].dt : null}
                t={t}
              />
            ))}
          </div>
        </>
      );
    }
  } else {
    if (dailyQ.isFetching && !dailyQ.data) {
      body = (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}>
          <Spin size="large" />
        </div>
      );
    } else if (dailyQ.isError) {
      body = <Empty description={t('kpi.weatherError')} />;
    } else if (!dailyQ.data || dailyRows.length === 0) {
      body = <Empty description={t('kpi.weatherForecastEmpty')} />;
    } else {
      body = (
        <div
          className="weather-forecast-strip"
          style={{
            display: 'flex',
            gap: 10,
            overflowX: 'auto',
            paddingBottom: 6,
            scrollSnapType: 'x mandatory',
            WebkitOverflowScrolling: 'touch',
          }}
        >
          {dailyRows.map((row) => (
            <DailyForecastCard key={row.key} row={row} t={t} />
          ))}
        </div>
      );
    }
  }

  return (
    <Modal
      open={open}
      onCancel={onClose}
      afterOpenChange={handleAfterModalOpenChange}
      footer={null}
      closable={false}
      centered={false}
      width={Math.min(720, typeof window !== 'undefined' ? window.innerWidth - 32 : 720)}
      zIndex={1200}
      destroyOnHidden
      rootClassName="map-detail-modal map-detail-modal--rain weather-forecast-modal"
      title={
        <div className="map-detail-modal__hero">
          <div className="map-detail-modal__hero-inner">
            <div className="map-detail-modal__hero-icon">
              <CloudSun size={20} color="var(--accent-blue)" />
            </div>
            <div className="map-detail-modal__hero-text">
              <div className="map-detail-modal__hero-title">{titleMain}</div>
            </div>
          </div>
          <button
            type="button"
            className="map-detail-modal__close-btn map-detail-modal__hero-close"
            onClick={onClose}
            aria-label={t('common.close')}
          >
            <X size={17} strokeWidth={2.25} />
          </button>
        </div>
      }
      styles={{
        header: { margin: 0, border: 'none', borderRadius: 0, background: 'transparent' },
        body: {
          background: 'transparent',
          padding: '16px 22px 22px',
          color: 'var(--text-primary)',
        },
        mask: { backdropFilter: 'blur(12px)', background: 'rgba(2, 6, 23, 0.72)' },
      }}
    >
      <WeatherLocationSearch
        onSelect={(lat, lng) => {
          dispatch(setWeatherPoint({ lat, lng, userPinned: true }));
        }}
        style={{ marginBottom: 20 }}
      />
      {viewSwitch}
      {body}
    </Modal>
  );
}
